import React, { useContext, useEffect, useState, useRef } from "react";
import { global } from "../../Context/Context";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from "recharts";
import adminStorage from "../../utils/adminStorage";

// Animated number hook
const useAnimatedCount = (target, duration = 900) => {
  const [count, setCount] = useState(0);
  const raf = useRef();
  useEffect(() => {
    let start = null;
    let from = 0;
    if (typeof target !== "number") return setCount(0);
    const animate = (timestamp) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setCount(Math.floor(progress * (target - from) + from));
      if (progress < 1) raf.current = requestAnimationFrame(animate);
      else setCount(target);
    };
    raf.current = requestAnimationFrame(animate);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return count;
};

const colorPallete = [
  "linear-gradient(135deg, #3CB371 55%, #43e97b 100%)",    // Green
  "linear-gradient(135deg, #329DFF 50%, #5FE1FF 100%)",    // Blue
  "linear-gradient(135deg, #F67280 60%, #FFAF7B 100%)",    // Coral
  "linear-gradient(135deg, #FFC300 60%, #FFD452 100%)",    // Gold
];

const iconBgPallete = [
  "rgba(52,201,123,0.18)", "#eaf6ff", "#fff2f2", "#fff8e1"
];

const Dashboard = () => {
  const baseUrl = process.env.REACT_APP_BASEURL1;
  const { users, user, hostCounts, viewhostCounts, tusers, settusers } = useContext(global);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 8;

  // Animated counters
  const totalUserCount = useAnimatedCount(Number(tusers || 0));
  const approvedCount = useAnimatedCount(Number(hostCounts.acc || 0));
  const pendingCount = useAnimatedCount(Number(hostCounts.pen || 0));
  const rejectedCount = useAnimatedCount(Number(hostCounts.rej || 0));

  useEffect(() => {
    if (!adminStorage.getItem("data")) window.location.href = "/";
    users();
    viewhostCounts();
  }, []);

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = user.length > 0 && user;

  // Unique CSS (injected just once)
  useEffect(() => {
    if (!document.getElementById("dashboard-unique-styles-v2")) {
      const style = document.createElement("style");
      style.id = "dashboard-unique-styles-v2";
      style.innerHTML = `
        .dashboard-animated-welcome {
          font-size: 2.3rem;
          font-weight: 900;
          color: #4326c4;
          margin-bottom: 2.3rem;
          letter-spacing: .01em;
          animation: dashFadeInUp .85s cubic-bezier(.8,0,.15,1);
          text-align: center;
          background: linear-gradient(90deg, #765dff 40%, #FFD452 90%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          padding-top: 1.2rem;
        }
        @keyframes dashFadeInUp {
          0% { opacity: 0; transform: translateY(60px);}
          60% { opacity: 1; transform: translateY(-7px);}
          100% { opacity: 1; transform: translateY(0);}
        }
        .dashboard-info-row {
          display: flex; gap: 20px;
          margin-bottom: 32px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .dashboard-info-box {
          flex: 1 1 220px;
          background: #fff;
          border-radius: 23px;
          box-shadow: 0 6px 32px #21196e1a;
          display: flex;
          align-items: center;
          min-width: 195px;
          min-height: 112px;
          margin-bottom: 1.5rem;
          padding: 1.5rem 1.1rem 1.5rem 1.2rem;
          position: relative;
          overflow: hidden;
          animation: dashFadeInUp .83s cubic-bezier(.7,0,.3,1);
          transition: box-shadow .18s, transform .2s;
          z-index: 1;
        }
        .dashboard-info-box:hover {
          box-shadow: 0 8px 42px #5632f03a;
          transform: translateY(-3px) scale(1.03);
        }
        .dashboard-info-icon {
          width: 58px; height: 58px;
          font-size: 2.35rem;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1.15rem;
          flex-shrink: 0;
          box-shadow: 0 2px 8px #00000011;
          background: #f8f8f8;
        }
        .dashboard-info-content {
          flex: 1 1 auto;
          min-width: 0;
        }
        .dashboard-info-title {
          font-size: 1.03rem;
          font-weight: 700;
          margin-bottom: 0.48em;
          color: #463482;
          letter-spacing: .01em;
        }
        .dashboard-info-number {
          font-size: 2.18rem;
          font-weight: 900;
          letter-spacing: .01em;
          color: #24212f;
          text-shadow: 0 1px 4px #3332ff09;
        }
        .dashboard-info-gradient-0 {
          background: ${colorPallete[0]};
        }
        .dashboard-info-gradient-1 {
          background: ${colorPallete[1]};
        }
        .dashboard-info-gradient-2 {
          background: ${colorPallete[2]};
        }
        .dashboard-info-gradient-3 {
          background: ${colorPallete[3]};
        }
        .dashboard-info-icon-bg-0 { background: ${iconBgPallete[0]}; }
        .dashboard-info-icon-bg-1 { background: ${iconBgPallete[1]}; }
        .dashboard-info-icon-bg-2 { background: ${iconBgPallete[2]}; }
        .dashboard-info-icon-bg-3 { background: ${iconBgPallete[3]}; }
        /* Bar chart container */
        .dashboard-graph-container {
          background: #fff;
          border-radius: 22px;
          box-shadow: 0 4px 26px #4036b11a;
          padding: 1.3rem 1rem .7rem 1rem;
          margin-bottom: 2.2rem;
        }
        /* Users list */
        .users-list {
          display: flex;
          flex-wrap: wrap;
          gap: 1.25rem;
          margin: 1.3rem 0 1.7rem 0;
          justify-content: flex-start;
          padding: 0;
          background: none;
        }
        .users-list li {
          background: #fff;
          border-radius: 18px;
          box-shadow: 0 2px 10px #3131741a;
          padding: 1.2rem .85rem .6rem .85rem;
          min-width: 120px;
          max-width: 144px;
          width: 100%;
          list-style: none;
          display: flex;
          flex-direction: column;
          align-items: center;
          transition: box-shadow .2s;
          animation: dashFadeInUp .8s cubic-bezier(.6,0,.2,1);
          opacity: 0;
          transform: translateY(40px);
          animation-fill-mode: forwards;
          animation-delay: .16s;
        }
        .users-list li img {
          border-radius: 50%;
          box-shadow: 0 2px 18px #aa97eb2b;
          width: 70px; height: 70px;
          object-fit: cover;
          margin-bottom: 10px;
        }
        .users-list li .users-list-name {
          font-weight: 700;
          font-size: 1.01rem;
          color: #5432e0;
          margin-bottom: 2px;
          text-align: center;
        }
        .users-list li .users-list-date {
          font-size: .87rem;
          color: #6c77a0;
          text-align: center;
        }
        .dashboard-latest-members-title {
          background: #6d35b9;
          color: #fff;
          font-weight: 700;
          border-radius: 20px 20px 0 0;
          font-size: 1.15rem;
          padding: .78rem 1.5rem .7rem .8rem;
          margin-bottom: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          letter-spacing: .01em;
        }
        .dashboard-new-label {
          font-size: 0.98rem;
          background: #ff735c;
          color: #fff;
          font-weight: 600;
          padding: .3em 1.1em;
          border-radius: 6px;
          margin-left: 8px;
          letter-spacing: .03em;
        }
        .dashboard-latest-members-box {
          background: #fff;
          border-radius: 0 0 20px 20px;
          box-shadow: 0 2px 12px #7852ea16;
          padding: .5rem .5rem 0 .5rem;
          min-height: 180px;
        }
        .box-footer.text-center {
          padding: 12px 0 8px 0;
        }
        .uppercase { text-transform: uppercase; letter-spacing: .05em;}
        @media (max-width: 1080px) {
          .dashboard-info-row { flex-direction: column; gap: 12px;}
          .dashboard-info-box { min-height: 70px; padding: .8rem .5rem;}
        }
        @media (max-width: 600px) {
          .dashboard-animated-welcome { font-size: 1.17rem; }
          .dashboard-info-row { flex-direction: column; gap: 12px;}
          .dashboard-info-box { min-width: 120px; min-height: 64px; padding: .7rem .32rem; }
          .dashboard-info-icon { width: 36px;height:36px; font-size:1.12rem; }
          .dashboard-info-title { font-size: .79rem;}
          .dashboard-info-number { font-size: 1.14rem;}
          .users-list li { min-width: 70px; max-width:104px; padding: .49rem .23rem;}
          .users-list li img { width: 40px;height:40px;}
          .users-list li .users-list-name { font-size:.7rem;}
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Animate user cards after render
  useEffect(() => {
    setTimeout(() => {
      document.querySelectorAll(".users-list li").forEach((li, idx) => {
        li.style.opacity = 1;
        li.style.transform = "translateY(0)";
        li.style.animationDelay = `${0.11 * idx}s`;
      });
    }, 80);
  }, [currentUsers]);

  // Data for the bar chart
  const barData = [
    { name: "Total Users", value: totalUserCount, fill: "#30ca81" },
    { name: "Approved", value: approvedCount, fill: "#28a0e2" },
    { name: "Pending", value: pendingCount, fill: "#f46a7a" },
    { name: "Rejected", value: rejectedCount, fill: "#ffc000" },
  ];

  // Info box icons (use react-icons for best, here font-awesome fallback)
  const icons = [
    <i className="fa fa-users" style={{ color: "#fff951" }} />,
    <i className="fa fa-user" style={{ color: "#fff" }} />,
    <i className="fa fa-user-clock" style={{ color: "#fff" }} />,
    <i className="fa fa-user-times" style={{ color: "#fff" }} />,
  ];

  const infoTitles = [
    "Total User",
    "Total Approved Host",
    "Total Pending Host",
    "Total Rejected Host"
  ];

  const infoNumbers = [
    totalUserCount,
    approvedCount,
    pendingCount,
    rejectedCount
  ];

  return (
    <>
      <section className="content-header">
        <div className="dashboard-animated-welcome">
          <span role="img" aria-label="wave">👋</span> Welcome to <b>Master Panel</b>
        </div>
        <ol className="breadcrumb" style={{ background: "none", marginBottom: 0 }}>
          <li>
            <a href="#" style={{ color: "#1e7ebf" }}>Home</a>
          </li>
          <li className="active" style={{ color: "#777" }}>Dashboard</li>
        </ol>
      </section>
      <section className="content">
        {/* Info boxes */}
        <div className="dashboard-info-row">
          {[0,1,2,3].map(idx => (
            <div className={`dashboard-info-box dashboard-info-gradient-${idx}`} key={idx}>
              <div className={`dashboard-info-icon dashboard-info-icon-bg-${idx}`}>
                {icons[idx]}
              </div>
              <div className="dashboard-info-content">
                <div className="dashboard-info-title">{infoTitles[idx]}</div>
                <div className="dashboard-info-number">{infoNumbers[idx].toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Bar Chart */}
        <div className="dashboard-graph-container" style={{ marginBottom: "40px" }}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData} barSize={55} margin={{ top: 20, right: 28, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 6" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#6540c9", fontWeight: 700, fontSize: 14 }} />
              <YAxis tick={{ fill: "#8688af", fontSize: 13 }} />
              <Tooltip />
              <Bar dataKey="value">
                <LabelList dataKey="value" position="top" style={{ fontWeight: 900, fill: "#4338CA" }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Latest Members */}
        <div className="dashboard-latest-members-title">
          Latest Members
          <span className="dashboard-new-label">8 New Members</span>
        </div>
        <div className="dashboard-latest-members-box">
          <ul className="users-list clearfix">
            {Array.isArray(currentUsers) &&
              currentUsers.slice(indexOfFirstUser, indexOfLastUser).map((item, index) => (
                <li key={item._id || index}>
                  <img
                    className="dummyImage"
                    src={
                      item.picture && `${item.picture}` !== ""
                        ? `${baseUrl}${item.picture}`
                        : "/mastiLive_logo.png"
                    }
                    alt="User"
                  />
                  <div className="users-list-name">{item.username}</div>
                  <div className="users-list-date">
                    {item.createdAt ? String(item.createdAt).substring(0, 10) : ""}
                  </div>
                </li>
              ))}
          </ul>
        </div>
        <div className="box-footer text-center">
          <Link to="/admin/manageUser" className="uppercase">
            View All Users
          </Link>
        </div>
      </section>
    </>
  );
};

export default Dashboard;
